import { useContext, useMemo, useState } from "react";
import {
  Sprout,
  MapPin,
  Coins,
  Layers,
  TrendingUp,
  CalendarDays,
  Tag,
  ImagePlus,
  Pencil,
  Ban,
  RotateCcw,
} from "lucide-react";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { useApi } from "../../hooks/useApi";
import { useAuthedApi } from "../../hooks/useAuthedApi";
import { normalizeCategories } from "../../utils/categoryHelpers";
import LoadingState from "../../components/LoadingState";
import { T } from "../../constants/theme";

const EMPTY_FORM = {
  name: "",
  description: "",
  price: "",
  stock: "",
  category_id: "",
  location: "",
  funding_goal: "",
  roi_percent: "",
  duration_days: "",
  project_type: "",
};

const PROJECT_TYPES = [
  { value: "tea", label: "Tea" },
  { value: "corn", label: "Corn" },
  { value: "cinnamon", label: "Cinnamon" },
  { value: "coconut", label: "Coconut" },
];

const FIELD_ICON_SIZE = 15;

function Field({ icon, label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: ".04em",
          textTransform: "uppercase",
          color: T.muted,
          marginBottom: 8,
        }}
      >
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}

export default function FarmerDashboardPage() {
  const { user, token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [editingId, setEditingId] = useState(null);

  const { data: categoriesData } = useApi("/categories");
  const categories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData],
  );

  const { data: projectsData, loading } = useAuthedApi(
    "/farmer/projects",
    token,
    [reloadKey],
  );
  const projects = projectsData?.data || [];

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const refresh = () => setReloadKey((k) => k + 1);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImage(null);
    setImagePreview("");
    setEditingId(null);
  };

  const pickImage = (file) => {
    setImage(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : "");
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setForm({
      name: project.name || "",
      description: project.description || "",
      price: project.price ?? "",
      stock: project.stock ?? "",
      category_id: project.category_id ?? "",
      location: project.location || "",
      funding_goal: project.funding_goal ?? "",
      roi_percent: project.roi_percent ?? "",
      duration_days: project.duration_days ?? "",
      project_type: project.project_type || "",
    });
    setImage(null);
    setImagePreview(project.image_url || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadProjectImage = async (file, projectId) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await apiFetch(
      `/farmer/projects/${projectId}/image`,
      { method: "POST", body: formData },
      { authToken: token },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.error || "Image upload failed");
    return data;
  };

  const submit = async () => {
    if (!form.name.trim() || !form.price) {
      toast("Name and price per share are required", "error");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        price: Number(form.price),
        stock: form.stock ? Number(form.stock) : 0,
        category_id: form.category_id ? Number(form.category_id) : null,
        location: form.location.trim() || null,
        funding_goal: form.funding_goal ? Number(form.funding_goal) : null,
        roi_percent: form.roi_percent ? Number(form.roi_percent) : null,
        duration_days: form.duration_days ? Number(form.duration_days) : null,
        project_type: form.project_type.trim() || null,
      };

      const res = await apiFetch(
        editingId ? `/farmer/projects/${editingId}` : "/farmer/projects",
        {
          method: editingId ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        { authToken: token },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || body.error || "Unable to save project");

      const projectId = editingId || body.id;
      if (image) await uploadProjectImage(image, projectId);

      toast(editingId ? "Project updated" : "Project listed");
      resetForm();
      refresh();
    } catch (e) {
      toast(e.message || "Failed to save project", "error");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (project, project_status) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/farmer/projects/${project.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ project_status }),
        },
        { authToken: token },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || body.error || "Unable to update project");
      toast(project_status === "cancelled" ? "Project cancelled" : "Project reactivated");
      refresh();
    } catch (e) {
      toast(e.message || "Failed to update project", "error");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    background: T.paper,
    color: T.ink,
    fontSize: 14,
    outline: "none",
  };

  const primaryBtn = {
    background: `linear-gradient(135deg, ${T.gold}, ${T.goldHover})`,
    color: T.white,
    border: "none",
    borderRadius: 12,
    padding: "14px 28px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    boxShadow: T.shadowSoft,
    transition: "transform .2s ease, box-shadow .2s ease",
  };

  const ghostBtn = {
    background: "transparent",
    color: T.ink,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: "14px 24px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 80px" }}>
      <style>{`
        .farmer-input:focus, .farmer-select:focus, .farmer-textarea:focus {
          border-color: ${T.gold} !important;
          box-shadow: 0 0 0 3px ${T.goldLight};
        }
        .farmer-btn-primary:hover { transform: translateY(-2px); box-shadow: ${T.shadowMedium}; }
        .farmer-card:hover { border-color: rgba(95,203,140,0.35) !important; }
        .farmer-drop:hover { border-color: ${T.gold} !important; background: ${T.goldLight} !important; }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 6,
        }}
      >
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: T.goldLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Sprout size={24} color={T.gold} />
        </div>
        <h1
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            color: T.ink,
            margin: 0,
          }}
        >
          My Farm
        </h1>
      </div>
      <p style={{ color: T.muted, marginBottom: 32 }}>
        List a field project for investors to fund, and manage the ones you've
        already listed, {user?.first_name}.
      </p>

      <div
        style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 20,
          padding: 32,
          marginBottom: 48,
          boxShadow: T.shadowSoft,
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: 24,
            color: T.ink,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {editingId ? <Pencil size={18} color={T.gold} /> : <Sprout size={18} color={T.gold} />}
          {editingId ? "Edit Project" : "List a New Field Project"}
        </h3>

        <Field icon={<Tag size={FIELD_ICON_SIZE} color={T.gold} />} label="Project Name">
          <input
            className="farmer-input"
            style={inputStyle}
            placeholder="e.g. Corn Field #12"
            value={form.name}
            onChange={set("name")}
          />
        </Field>

        <Field icon={<Layers size={FIELD_ICON_SIZE} color={T.gold} />} label="Description">
          <textarea
            className="farmer-textarea"
            style={{ ...inputStyle, minHeight: 90, resize: "vertical" }}
            placeholder="Tell investors what makes this field worth backing"
            value={form.description}
            onChange={set("description")}
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Field icon={<Coins size={FIELD_ICON_SIZE} color={T.gold} />} label="Price per Share (LKR)">
            <input
              className="farmer-input"
              style={inputStyle}
              type="number"
              placeholder="10.00"
              value={form.price}
              onChange={set("price")}
            />
          </Field>
          <Field icon={<Layers size={FIELD_ICON_SIZE} color={T.gold} />} label="Total Shares Available">
            <input
              className="farmer-input"
              style={inputStyle}
              type="number"
              placeholder="100"
              value={form.stock}
              onChange={set("stock")}
            />
          </Field>
          <Field icon={<Sprout size={FIELD_ICON_SIZE} color={T.gold} />} label="Category">
            <select
              className="farmer-select"
              style={inputStyle}
              value={form.category_id}
              onChange={set("category_id")}
            >
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field icon={<MapPin size={FIELD_ICON_SIZE} color={T.gold} />} label="Location">
            <input
              className="farmer-input"
              style={inputStyle}
              placeholder="e.g. Kalutara, Sri Lanka"
              value={form.location}
              onChange={set("location")}
            />
          </Field>
          <Field icon={<Coins size={FIELD_ICON_SIZE} color={T.gold} />} label="Funding Goal (LKR)">
            <input
              className="farmer-input"
              style={inputStyle}
              type="number"
              placeholder="1000"
              value={form.funding_goal}
              onChange={set("funding_goal")}
            />
          </Field>
          <Field icon={<TrendingUp size={FIELD_ICON_SIZE} color={T.gold} />} label="Target ROI (%)">
            <input
              className="farmer-input"
              style={inputStyle}
              type="number"
              placeholder="8"
              value={form.roi_percent}
              onChange={set("roi_percent")}
            />
          </Field>
          <Field icon={<CalendarDays size={FIELD_ICON_SIZE} color={T.gold} />} label="Duration (days)">
            <input
              className="farmer-input"
              style={inputStyle}
              type="number"
              placeholder="90"
              value={form.duration_days}
              onChange={set("duration_days")}
            />
          </Field>
          <Field icon={<Tag size={FIELD_ICON_SIZE} color={T.gold} />} label="Crop Type">
            <select
              className="farmer-select"
              style={inputStyle}
              value={form.project_type}
              onChange={set("project_type")}
            >
              <option value="">Select Crop Type</option>
              {PROJECT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              color: T.muted,
              marginBottom: 8,
            }}
          >
            <ImagePlus size={FIELD_ICON_SIZE} color={T.gold} />
            Project Photo
          </label>
          <label
            className="farmer-drop"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              border: `1.5px dashed ${T.border}`,
              borderRadius: 14,
              padding: 16,
              cursor: "pointer",
              transition: "all .2s ease",
              background: "transparent",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 14,
                overflow: "hidden",
                background: T.paper,
                border: `1px solid ${T.border}`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <ImagePlus size={22} color={T.muted} />
              )}
            </div>
            <div style={{ fontSize: 13, color: T.muted }}>
              {image ? image.name : "Click to upload a photo of the field"}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => pickImage(e.target.files?.[0] || null)}
              style={{ display: "none" }}
            />
          </label>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            className="farmer-btn-primary"
            onClick={submit}
            disabled={busy}
            style={primaryBtn}
          >
            {editingId ? "Save Changes" : "List Project"}
          </button>
          {editingId && (
            <button onClick={resetForm} style={ghostBtn}>
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      <h3 style={{ color: T.ink, display: "flex", alignItems: "center", gap: 8 }}>
        <Layers size={18} color={T.gold} />
        My Projects
      </h3>
      {loading ? (
        <LoadingState />
      ) : projects.length === 0 ? (
        <div
          style={{
            color: T.muted,
            border: `1px dashed ${T.border}`,
            borderRadius: 16,
            padding: 32,
            textAlign: "center",
          }}
        >
          You haven't listed any projects yet.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {projects.map((p) => {
            const isCancelled = p.project_status === "cancelled";
            return (
              <div
                key={p.id}
                className="farmer-card"
                style={{
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  padding: 20,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 16,
                  flexWrap: "wrap",
                  transition: "border-color .2s ease",
                }}
              >
                <div style={{ display: "flex", gap: 16, flex: "1 1 320px", minWidth: 0 }}>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 14,
                      overflow: "hidden",
                      background: T.paper,
                      border: `1px solid ${T.border}`,
                      boxShadow: T.shadowSoft,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Sprout size={26} color={T.muted} />
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 700, color: T.ink }}>{p.name}</div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: ".04em",
                          textTransform: "uppercase",
                          padding: "3px 10px",
                          borderRadius: 999,
                          color: isCancelled ? "#e07a5f" : T.gold,
                          background: isCancelled ? "rgba(224,122,95,0.12)" : T.goldLight,
                        }}
                      >
                        {p.project_status}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
                      {p.category_name || "Uncategorized"} · LKR {Number(p.price).toFixed(2)}/share
                    </div>
                    {p.funding_goal > 0 && (
                      <div style={{ marginTop: 10, maxWidth: 320 }}>
                        <div style={{ height: 5, borderRadius: 3, background: T.paper, overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              width: `${Math.min(100, (Number(p.funding_raised || 0) / Number(p.funding_goal)) * 100)}%`,
                              background: T.gold,
                            }}
                          />
                        </div>
                        <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                          LKR {Number(p.funding_raised || 0).toLocaleString()} raised of LKR{" "}
                          {Number(p.funding_goal).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => startEdit(p)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      background: "transparent",
                      color: T.ink,
                      border: `1px solid ${T.border}`,
                      borderRadius: 10,
                      padding: "9px 16px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <Pencil size={14} />
                    Edit
                  </button>
                  {isCancelled ? (
                    <button
                      onClick={() => setStatus(p, "active")}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: T.gold,
                        color: T.white,
                        border: "none",
                        borderRadius: 10,
                        padding: "9px 16px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      <RotateCcw size={14} />
                      Reactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus(p, "cancelled")}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "transparent",
                        color: "#e07a5f",
                        border: "1px solid #e07a5f",
                        borderRadius: 10,
                        padding: "9px 16px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <Ban size={14} />
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
